"use client";

import { Fragment, useEffect, useRef, useState } from "react";
import {
  BORDER,
  FONT_MONO,
  TEXT,
  TEXT_DIM,
  TEXT_MUTE,
  VIOLET,
  VIOLET_BRIGHT,
  VIOLET_GLOW,
  VIOLET_SOFT,
} from "@/components/ui/organisms/chord-practice/palette";
import { useChordLabel, useNoteLabel } from "@/hooks/use-notation";

export interface ProgressionSlot {
  /** Note names of the chord, ordered (root first). */
  notes: string[];
  /** Pitch class of the root, the note painted in violet gradient. */
  rootPc: number;
  /** Pitch classes of all chord notes (parallel to `notes`). */
  pcs: number[];
  /** Roman numeral with optional accidentals (I, ii, vii°, …). */
  roman: string;
  /** Compact symbol (Cmaj, Dm, …). */
  symbol: string;
}

interface ProgressionStageProps {
  /** All slots of the progression, in order. */
  slots: ProgressionSlot[];
  /** Current chord index inside `slots`. */
  currentStep: number;
}

/** Width in pixels between two adjacent slots in the queue. */
const SLOT_SPACING = 280;
/** Slide animation duration when `currentStep` changes. */
const SLIDE_MS = 520;
/** Visible window, slots rendered at relative offsets from current. */
const VISIBLE_OFFSETS = [-1, 0, 1, 2] as const;

/**
 * Sliding-queue rendering of a progression. The chord at the center is
 * the one to play right now ; the next ones drift to the right with
 * decreasing scale and opacity, mimicking a tempo-drill stage.
 *
 * Used as a fallback inside `ProgressionDrill` when the voice-leading
 * `ProgressionTransition` cannot be rendered (minor mode or no
 * hand-picked voicing for the progression).
 */
export function ProgressionStage({
  slots,
  currentStep,
}: ProgressionStageProps) {
  const chordLabel = useChordLabel();
  const toNoteLabel = useNoteLabel();
  const [slideOffset, setSlideOffset] = useState(0);
  const [animating, setAnimating] = useState(false);
  const prevStepRef = useRef(currentStep);

  useEffect(() => {
    const prev = prevStepRef.current;

    if (prev === currentStep) {
      return;
    }

    const direction = currentStep > prev ? 1 : -1;
    // Loop wrap : if we jumped from last to first, treat as forward.
    const wrap =
      Math.abs(currentStep - prev) === slots.length - 1
        ? -direction
        : direction;

    prevStepRef.current = currentStep;
    setAnimating(true);
    setSlideOffset(-wrap * SLOT_SPACING);

    const t = window.setTimeout(() => {
      setAnimating(false);
      setSlideOffset(0);
    }, SLIDE_MS);

    return () => window.clearTimeout(t);
  }, [currentStep, slots.length]);

  const edgeMask =
    "linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)";

  return (
    <div
      className="relative z-10 flex flex-1 flex-col items-center justify-center"
      style={{ padding: "20px 40px", minHeight: 0 }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 980,
          height: 240,
          position: "relative",
          overflow: "hidden",
          maskImage: edgeMask,
          WebkitMaskImage: edgeMask,
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 0,
            bottom: 30,
            left: "50%",
            width: 1,
            borderLeft: `1px dashed ${VIOLET_GLOW}`,
            opacity: 0.4,
            pointerEvents: "none",
          }}
        />

        {VISIBLE_OFFSETS.map((rel) => {
          const idx =
            (((currentStep + rel) % slots.length) + slots.length) %
            slots.length;
          const slot = slots[idx]!;
          const baseX = rel * SLOT_SPACING;
          const effectiveX = baseX + slideOffset;

          let scale = 0.55;
          let opacity = 0.4;
          let blur = 0.4;
          let label = "";
          let isCurrent = false;

          if (Math.abs(effectiveX) < 1) {
            scale = 1;
            opacity = 1;
            blur = 0;
            label = "▼ MAINTENANT";
            isCurrent = true;
          } else if (Math.abs(effectiveX - SLOT_SPACING) < 1) {
            scale = 0.62;
            opacity = 0.55;
            label = "PROCHAIN";
            blur = 0.2;
          } else if (Math.abs(effectiveX - 2 * SLOT_SPACING) < 1) {
            scale = 0.42;
            opacity = 0.22;
            label = "PUIS";
            blur = 0.6;
          }

          const transition = animating
            ? `transform ${SLIDE_MS}ms cubic-bezier(0.65, 0, 0.35, 1), opacity ${SLIDE_MS}ms cubic-bezier(0.65, 0, 0.35, 1), filter ${SLIDE_MS}ms`
            : "none";

          return (
            <div
              key={`${idx}-${rel}`}
              style={{
                position: "absolute",
                bottom: 30,
                left: "50%",
                transform: `translateX(calc(-50% + ${effectiveX}px)) scale(${scale})`,
                opacity,
                transformOrigin: "bottom center",
                filter: blur ? `blur(${blur}px)` : "none",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 10,
                transition,
              }}
            >
              <div
                style={{
                  fontFamily: FONT_MONO,
                  fontSize: 10,
                  color: isCurrent ? VIOLET_BRIGHT : TEXT_MUTE,
                  letterSpacing: "0.18em",
                  fontWeight: 600,
                  height: 14,
                  whiteSpace: "nowrap",
                }}
              >
                {label}
              </div>

              <div
                style={{
                  fontSize: 132,
                  fontWeight: 200,
                  lineHeight: 0.9,
                  letterSpacing: "-0.05em",
                  color: isCurrent ? TEXT : TEXT_DIM,
                  fontFamily: "Inter, sans-serif",
                  whiteSpace: "nowrap",
                }}
              >
                {chordLabel(slot.symbol)}
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 4,
                  fontSize: 22,
                  fontFamily: FONT_MONO,
                  fontWeight: 500,
                  color: isCurrent ? TEXT_DIM : TEXT_MUTE,
                  letterSpacing: "0.04em",
                  whiteSpace: "nowrap",
                }}
              >
                {slot.notes.map((n, ni) => {
                  const isRoot = slot.pcs[ni] === slot.rootPc;
                  const noteStyle: React.CSSProperties = isRoot
                    ? {
                        background: `linear-gradient(180deg, ${VIOLET_BRIGHT} 0%, ${VIOLET} 100%)`,
                        WebkitBackgroundClip: "text",
                        backgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        fontWeight: 700,
                      }
                    : {};

                  return (
                    <Fragment key={ni}>
                      {ni > 0 && (
                        <span
                          style={{
                            color: TEXT_MUTE,
                            margin: "0 6px",
                          }}
                        >
                          ·
                        </span>
                      )}
                      <span style={noteStyle}>{toNoteLabel(n)}</span>
                    </Fragment>
                  );
                })}
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span
                  style={{
                    padding: "3px 10px",
                    borderRadius: 4,
                    background: isCurrent
                      ? VIOLET_SOFT
                      : "rgba(255,255,255,0.03)",
                    border: `1px solid ${isCurrent ? VIOLET_GLOW : BORDER}`,
                    fontFamily: FONT_MONO,
                    fontSize: 10,
                    color: isCurrent ? VIOLET_BRIGHT : TEXT_MUTE,
                    letterSpacing: "0.12em",
                    fontWeight: 600,
                    textTransform: "uppercase",
                  }}
                >
                  {slot.roman}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
